import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import { PageLayout } from '@/components/ui/PageLayout';
import { LoadingState } from '@/components/AsyncStates';
import Icon from '@/components/AppIcon';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import WorkloadTable from './WorkloadTable';
import { useRefereeWorkload } from './useRefereeWorkload';
import { formatLocalDateParam } from '@/utils/apiDate';

// NEVER toISOString() for a date param — east of UTC it resolves to the previous day.
function getDefaultStartDate(): string {
  const now = new Date();
  return formatLocalDateParam(new Date(now.getFullYear(), now.getMonth(), 1));
}

function getDefaultEndDate(): string {
  return formatLocalDateParam(new Date());
}

const RefereeWorkload: React.FC = () => {
  const { t } = useTranslation('tournament');

  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);

  const dateRange = useMemo(() => ({ start: startDate, end: endDate }), [startDate, endDate]);
  const { data, loading, error, refetch } = useRefereeWorkload(dateRange);

  // Changing either date re-runs the query, so `loading` goes true on EVERY keystroke that lands a
  // valid date. The page used to early-return a bare <PageLayout><LoadingState/></PageLayout>, which
  // unmounted the date inputs themselves: the field the user was typing into disappeared mid-edit
  // and the end date could not be set until the round trip finished. Filters now stay mounted
  // always; only the very first load (no rows yet) gets a spinner, later fetches just dim the table.
  const hasRows = data.length > 0;
  const isInitialLoad = loading && !hasRows;
  const isRefetching = loading && hasRows;

  return (
    <PageLayout
      title={t('Referee Workload')}
      description={t('View referee workload distribution and match assignments')}
      icon={BarChart3}
      accentColor="amber"
    >
      {/* Filters — never gated on `loading`; see the comment above. */}
      <section
        className="bg-card border border-border rounded-lg p-6 mb-6"
        data-testid="workload-filters"
      >
        {/* h2, not h3: PageLayout already renders the page h1 and there is no h2 between them. */}
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('Filters')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
          {/* ui/Input, not a bare <input> with a sibling <label>: the sibling had no htmlFor, so
              both date fields reached screen readers unnamed. Input generates the id/htmlFor pair. */}
          <div className="min-w-0">
            <Input
              type="date"
              label={t('Start Date')}
              value={startDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
              data-testid="filter-start-date"
            />
          </div>
          <div className="min-w-0">
            <Input
              type="date"
              label={t('End Date')}
              value={endDate}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
              data-testid="filter-end-date"
            />
          </div>
        </div>
      </section>

      {/* Error — same red panel + outline "Try Again" as tournament-list and referee-management.
          It used to be an underlined text link, giving the module two error vocabularies. */}
      {error && !isInitialLoad && (
        <div
          role="alert"
          className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3"
          data-testid="workload-error"
        >
          <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          <Button variant="outline" size="sm" onClick={refetch} className="ms-auto" data-testid="workload-retry">
            {t('Try Again')}
          </Button>
        </div>
      )}

      {/* Loading — first load only. */}
      {isInitialLoad && <LoadingState title={t('Loading data...')} />}

      {/* Table — stays mounted across refetches, dimmed rather than torn out. */}
      {!isInitialLoad && (
        <div
          className={cn(
            'transition-opacity duration-200 ease-out motion-reduce:transition-none',
            isRefetching && 'opacity-60 pointer-events-none',
          )}
          aria-busy={isRefetching}
        >
          <WorkloadTable data={data} />
        </div>
      )}
    </PageLayout>
  );
};

export default RefereeWorkload;
